/* InvoiceExpiryChip — countdown chip surfaced on every open invoice row.
 *
 * Phase IV (Invoice Validity). The chip:
 *   - Reads `invoice.expires_at` (ISO timestamp). If absent (legacy invoice
 *     issued before Phase IV) it renders nothing.
 *   - Shows time-to-expiry in a human format: "3d left", "11h left",
 *     "42min left".
 *   - Three color tones based on urgency:
 *       > 48 hours left   → muted slate (informational)
 *       2–48 hours left   → amber (warning)
 *       0 hours left      → rose (expired — should also be filtered out
 *                            by the auto-void sweeper but renders for any
 *                            edge-case still-pre-acceptance invoices)
 *   - Refreshes every 60 seconds so the countdown stays live without
 *     refreshing the page.
 *
 * Skipped intentionally for already-accepted invoices (paid, financed,
 * credit_terms, etc.) — once the buyer accepts, the price locks and the
 * expiry no longer matters.
 */
import { useEffect, useState } from "react";
import { Clock, AlertTriangle } from "lucide-react";

const TERMINAL_STATUSES = new Set([
  "paid", "financed", "credit_terms", "authorized", "voided", "cancelled", "refunded",
]);

export default function InvoiceExpiryChip({ invoice, compact = false }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  if (!invoice?.expires_at) return null;
  if (TERMINAL_STATUSES.has((invoice.status || "").toLowerCase())) return null;

  const dt = new Date(invoice.expires_at);
  if (Number.isNaN(dt.getTime())) return null;
  const msLeft = dt.getTime() - Date.now();
  const expired = msLeft <= 0;
  const hoursLeft = msLeft / 3_600_000;
  const tone =
    expired ? "bg-rose-500/15 text-rose-700 border-rose-500/30"
    : hoursLeft < 48 ? "bg-amber-500/15 text-amber-800 border-amber-500/30"
    : "bg-foreground/[0.05] text-foreground/65 border-foreground/15";

  const label = (() => {
    if (expired) return "Expired";
    if (hoursLeft < 1) {
      const m = Math.max(1, Math.floor(msLeft / 60_000));
      return `${m}min left`;
    }
    if (hoursLeft < 48) return `${Math.floor(hoursLeft)}h left`;
    const days = Math.ceil(hoursLeft / 24);
    return `${days}d left`;
  })();

  const Icon = expired ? AlertTriangle : Clock;
  const Wrapper = compact ? "span" : "div";

  return (
    <Wrapper
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${tone}`}
      title={expired
        ? `This invoice expired on ${dt.toLocaleString()} and is no longer accepting payment. Ask the seller to re-issue at current pricing.`
        : `Pay or authorize before ${dt.toLocaleString()} or this invoice will void`}
      data-testid={`invoice-expiry-chip-${invoice.id || invoice.code || "x"}`}
    >
      <Icon className="w-2.5 h-2.5" />
      {label}
    </Wrapper>
  );
}
